export const TEMPERATURE_RANGE_OPTIONS = [
  { id: "today", label: "Today" },
  { id: "yesterday", label: "Yesterday" },
  { id: "seven_days", label: "Last 7 days" },
  { id: "month", label: "Last month" },
];

export const TEMPERATURE_INTERVAL_OPTIONS = [
  { id: "one_minute", label: "1 min", minutes: 1 },
  { id: "five_minutes", label: "5 min", minutes: 5 },
  { id: "ten_minutes", label: "10 min", minutes: 10 },
  { id: "one_hour", label: "1 hr", minutes: 60 },
];

export const TEMPERATURE_BORDER_C = 1;

function niceTemperatureStep(span) {
  const rough = Math.max(span / 4, 0.1);
  const power = 10 ** Math.floor(Math.log10(rough));
  const normalized = rough / power;
  const multiplier = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return multiplier * power;
}

export function getTemperatureAxisBounds(readings, maximum, minimum = null) {
  const temperatures = (readings ?? []).map((reading) => Number(reading.temperature_c)).filter(Number.isFinite);
  if (!temperatures.length) return { minimum: 0, maximum: 1, step: 1 };
  let rawMinimum = Math.min(...temperatures);
  let rawMaximum = Math.max(...temperatures);
  const upperLimit = Number(maximum);
  const lowerLimit = minimum === null || minimum === "" ? null : Number(minimum);
  if (Number.isFinite(upperLimit)) {
    rawMinimum = Math.min(rawMinimum, upperLimit);
    rawMaximum = Math.max(rawMaximum, upperLimit);
  }
  if (Number.isFinite(lowerLimit)) {
    rawMinimum = Math.min(rawMinimum, lowerLimit);
    rawMaximum = Math.max(rawMaximum, lowerLimit);
  }
  const padding = Math.max(1, (rawMaximum - rawMinimum) * 0.14);
  const step = niceTemperatureStep(rawMaximum - rawMinimum + padding * 2);
  const axisMinimum = Math.floor((rawMinimum - padding) / step) * step;
  const calculatedMaximum = Math.ceil((rawMaximum + padding) / step) * step;
  const candidateMaximum = calculatedMaximum === 0 ? 0 : calculatedMaximum;
  const axisMaximum = Number.isFinite(candidateMaximum) && candidateMaximum > axisMinimum ? candidateMaximum : axisMinimum + step;
  return { minimum: axisMinimum, maximum: axisMaximum, step };
}

export function getTemperatureRangeBounds(range, now = new Date()) {
  const end = new Date(now);
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);

  if (range === "yesterday") {
    const start = new Date(startOfToday);
    start.setDate(start.getDate() - 1);
    return { start, end: startOfToday };
  }

  if (range === "seven_days") {
    return { start: new Date(end.getTime() - 7 * 24 * 60 * 60 * 1000), end };
  }

  if (range === "month") {
    return { start: new Date(end.getTime() - 30 * 24 * 60 * 60 * 1000), end };
  }

  return { start: startOfToday, end };
}

export function isTemperatureUnsafe(value, maximum, minimum = null) {
  const temperature = Number(value);
  if (!Number.isFinite(temperature)) return false;
  const maximumValue = Number(maximum);
  const minimumValue = minimum === null || minimum === "" ? null : Number(minimum);
  return (Number.isFinite(maximumValue) && temperature > maximumValue)
    || (Number.isFinite(minimumValue) && temperature < minimumValue);
}

export function getTemperatureCondition(device, borderC = TEMPERATURE_BORDER_C) {
  if (device?.current_temperature_c === null || device?.current_temperature_c === undefined || device?.current_temperature_c === "") {
    return { state: "unknown", label: "No reading" };
  }
  const value = Number(device?.current_temperature_c);
  const maximum = Number(device?.maximum_temperature_c);
  const minimum = device?.minimum_temperature_c === null || device?.minimum_temperature_c === ""
    ? null
    : Number(device?.minimum_temperature_c);

  if (!Number.isFinite(value) || (!Number.isFinite(maximum) && !Number.isFinite(minimum))) {
    return { state: "unknown", label: "No reading" };
  }

  const distancesOutside = [];
  if (Number.isFinite(maximum) && value > maximum) distancesOutside.push(value - maximum);
  if (Number.isFinite(minimum) && value < minimum) distancesOutside.push(minimum - value);
  if (distancesOutside.length) {
    return Math.max(...distancesOutside) > borderC
      ? { state: "critical", label: "Out of range" }
      : { state: "warning", label: "Near limit" };
  }

  const distancesInside = [];
  if (Number.isFinite(maximum)) distancesInside.push(maximum - value);
  if (Number.isFinite(minimum)) distancesInside.push(value - minimum);
  return Math.min(...distancesInside) <= borderC
    ? { state: "warning", label: "Near limit" }
    : { state: "healthy", label: "Within range" };
}

export function countTemperatureExcursions(readings, maximum, minimum = null) {
  let excursions = 0;
  let wasUnsafe = false;
  for (const reading of readings ?? []) {
    const unsafe = isTemperatureUnsafe(reading.temperature_c, maximum, minimum);
    if (unsafe && !wasUnsafe) excursions += 1;
    wasUnsafe = unsafe;
  }
  return excursions;
}

export function getTemperatureSummary(readings, maximum, minimum = null) {
  const values = (readings ?? []).map((reading) => Number(reading.temperature_c)).filter(Number.isFinite);
  const bounds = values.reduce((current, value) => ({
    lowest: current.lowest === null ? value : Math.min(current.lowest, value),
    highest: current.highest === null ? value : Math.max(current.highest, value),
  }), { lowest: null, highest: null });
  return {
    count: values.length,
    lowest: bounds.lowest,
    highest: bounds.highest,
    excursions: countTemperatureExcursions(readings, maximum, minimum),
  };
}

export function mergeTemperatureHistory(permanentReadings = [], heartbeatReadings = []) {
  const merged = new Map();
  for (const reading of [...heartbeatReadings, ...permanentReadings]) {
    const temperature = Number(reading.temperature_c);
    const timestamp = new Date(reading.recorded_at).getTime();
    if (!Number.isFinite(temperature) || !Number.isFinite(timestamp)) continue;
    const key = `${new Date(timestamp).toISOString()}:${temperature}`;
    merged.set(key, { temperature_c: temperature, recorded_at: new Date(timestamp).toISOString() });
  }
  return [...merged.values()].sort((left, right) => new Date(left.recorded_at) - new Date(right.recorded_at));
}

function safetyDistance(reading, maximum, minimum) {
  const value = Number(reading.temperature_c);
  const upper = Number(maximum);
  const lower = minimum === null || minimum === "" ? null : Number(minimum);
  const distances = [];
  if (Number.isFinite(upper)) distances.push(value - upper);
  if (Number.isFinite(lower)) distances.push(lower - value);
  return distances.length ? Math.max(...distances) : 0;
}

export function bucketTemperatureHistory(readings, intervalId, maximum, minimum = null) {
  const option = TEMPERATURE_INTERVAL_OPTIONS.find((candidate) => candidate.id === intervalId) ?? TEMPERATURE_INTERVAL_OPTIONS[0];
  const bucketSize = option.minutes * 60 * 1000;
  const buckets = new Map();
  for (const reading of readings ?? []) {
    const timestamp = new Date(reading.recorded_at).getTime();
    if (!Number.isFinite(timestamp) || !Number.isFinite(Number(reading.temperature_c))) continue;
    const key = Math.floor(timestamp / bucketSize);
    const existing = buckets.get(key);
    if (!existing || safetyDistance(reading, maximum, minimum) > safetyDistance(existing, maximum, minimum)) buckets.set(key, reading);
    else if (safetyDistance(reading, maximum, minimum) === safetyDistance(existing, maximum, minimum) && timestamp > new Date(existing.recorded_at).getTime()) buckets.set(key, reading);
  }
  return [...buckets.values()].sort((left, right) => new Date(left.recorded_at) - new Date(right.recorded_at));
}

export function buildIllustrativeHistory(device, range, now = new Date()) {
  const source = (device?.readings ?? []).map(Number).filter(Number.isFinite);
  if (!source.length) return [];
  const { start, end } = getTemperatureRangeBounds(range, now);
  const sampleCount = range === "month" ? 180 : range === "seven_days" ? 84 : 24;
  const span = Math.max(1, end.getTime() - start.getTime());
  return Array.from({ length: sampleCount }, (_, index) => ({
    temperature_c: source[index % source.length],
    recorded_at: new Date(start.getTime() + (span * index) / Math.max(1, sampleCount - 1)).toISOString(),
  }));
}
